import { easeInOut, motion } from 'motion/react'
import { Cookie, Eye, FileText, Globe, Lock, Scale, Shield, Users } from 'lucide-react'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

const policyHighlights = [
  {
    icon: Shield,
    title: 'Data Protection',
    description:
      'Your research data is encrypted and stored securely following industry best practices.',
  },
  {
    icon: Lock,
    title: 'Access Control',
    description: 'Strict authentication through Keycloak SSO protects your account and projects.',
  },
  {
    icon: Eye,
    title: 'Privacy First',
    description: 'We collect only essential data needed for service functionality - no tracking.',
  },
  {
    icon: Scale,
    title: 'GDPR Compliant',
    description: 'Full compliance with GDPR, CCPA, and other privacy regulations.',
  },
]

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

export default function Policies() {
  return (
    <PageLayout>
      <Seo
        title="Policies | SOTA Navigator"
        description="SOTA Navigator policies in one place: privacy, security, terms of service, cookies and data retention."
      />

      <div className="relative overflow-hidden bg-[#001871] text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2 }}
          className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
        />

        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1 }}
          className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />

        <div className="relative pb-40 pt-32 sm:pb-48 sm:pt-40">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="space-y-8"
              >
                <motion.div
                  variants={glowAnimation}
                  initial="initial"
                  animate="animate"
                  className="inline-flex items-center gap-2 rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                >
                  <Scale className="size-4" />
                  Legal & Privacy Information
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                  className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                >
                  Policies & Terms
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
                >
                  Our commitment to protecting your privacy and ensuring transparent terms of
                  service
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden leading-none">
          <svg
            className="relative block h-[50px] w-full"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </div>

      {/* Privacy Highlights */}
      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-[#001871]">Privacy & Security</h2>
            <p className="mx-auto max-w-3xl text-xl text-gray-600">
              Your privacy and data security are our top priorities. Here's how we protect your
              information.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {policyHighlights.map((highlight, index) => (
              <motion.div
                key={highlight.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="rounded-xl border border-gray-100 bg-white p-6 shadow-xs transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                    <highlight.icon className="size-10 text-[#001871]" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-[#001871]">{highlight.title}</h3>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cookies Section */}
      <section className="relative bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="mb-8 text-3xl font-bold text-[#001871]">Cookie Usage</h2>

              <div className="rounded-xl bg-white p-8 shadow-xs">
                <div className="flex items-start gap-4">
                  <Cookie className="size-8 shrink-0 text-[#001871]" />
                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">
                      We Use Only Essential Cookies
                    </h3>
                    <p className="mb-6 text-gray-700">
                      SOTA Navigator uses only essential cookies required for basic functionality.
                      We do not use analytics, tracking, or marketing cookies.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">Session Cookies (Required)</h4>
                        <p className="text-sm text-gray-600">
                          Used to maintain your logged-in state and manage your session securely.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Authentication Cookies (Required)
                        </h4>
                        <p className="text-sm text-gray-600">
                          Used by our Keycloak authentication system for secure login and access
                          control.
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4">
                      <div className="flex items-center gap-2">
                        <Shield className="size-4 text-green-600" />
                        <span className="font-medium text-green-800">Privacy-First Approach</span>
                      </div>
                      <p className="mt-1 text-sm text-green-700">
                        We don't track you, collect analytics, or use third-party advertising
                        cookies. Your research data stays private.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              <div>
                <h2 className="mb-8 text-3xl font-bold text-[#001871]">Terms of Service</h2>

                <div className="space-y-8 text-gray-700">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-start gap-4">
                      <Users className="size-6 shrink-0 text-[#001871]" />
                      <div>
                        <h3 className="mb-3 text-xl font-semibold text-[#001871]">Service Usage</h3>
                        <p>
                          By accessing and using SOTA Navigator, you agree to these terms of
                          service. Our service is intended for professional use in systematic
                          literature reviews and medical research.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-start gap-4">
                      <Lock className="size-6 shrink-0 text-[#001871]" />
                      <div>
                        <h3 className="mb-3 text-xl font-semibold text-[#001871]">
                          Account Security
                        </h3>
                        <p>
                          You are responsible for maintaining the confidentiality of your account
                          credentials and for all activities under your account. We use Keycloak for
                          secure authentication and session management.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-start gap-4">
                      <Shield className="size-6 shrink-0 text-[#001871]" />
                      <div>
                        <h3 className="mb-3 text-xl font-semibold text-[#001871]">
                          Data Processing & GDPR
                        </h3>
                        <p className="mb-4">
                          We process your data in accordance with GDPR requirements. This includes:
                        </p>
                        <ul className="list-inside list-disc space-y-2">
                          <li>Lawful, fair, and transparent processing</li>
                          <li>Purpose limitation and data minimization</li>
                          <li>Accuracy and storage limitation</li>
                          <li>Integrity and confidentiality</li>
                          <li>Accountability and documentation</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-start gap-4">
                      <FileText className="size-6 shrink-0 text-[#001871]" />
                      <div>
                        <h3 className="mb-3 text-xl font-semibold text-[#001871]">
                          Intellectual Property
                        </h3>
                        <p>
                          All content and functionality within SOTA Navigator is protected by
                          intellectual property rights. Users may not copy, modify, or distribute
                          our content without permission. Your research data remains your property.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-start gap-4">
                      <Globe className="size-6 shrink-0 text-[#001871]" />
                      <div>
                        <h3 className="mb-3 text-xl font-semibold text-[#001871]">
                          Third-Party Services
                        </h3>
                        <p className="mb-4">
                          We integrate with the following third-party services:
                        </p>
                        <ul className="list-inside list-disc space-y-2">
                          <li>
                            <strong>Keycloak:</strong> Authentication and user management
                          </li>
                          <li>
                            <strong>Azure Blob Storage:</strong> Secure file storage
                          </li>
                          <li>
                            <strong>Sentry:</strong> Error monitoring (privacy-focused)
                          </li>
                          <li>
                            <strong>DOI Services:</strong> CrossRef, PubMed, Semantic Scholar for
                            metadata
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative bg-[#001871] py-24 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl text-center"
          >
            <h2 className="mb-8 text-4xl font-bold [text-shadow:0_1px_2px_rgb(0_0_0/20%)]">
              Questions About Our Policies?
            </h2>
            <p className="mb-8 text-xl text-white/90">
              Our Data Protection Officer is here to help. Contact us for any privacy-related
              questions or to exercise your data rights.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <a
                href="mailto:privacy@sotanavigator.com"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-medium text-[#001871] transition-colors hover:bg-white/90"
              >
                Email Privacy Team
              </a>
              <span className="text-white/80">Response within 24 hours</span>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  )
}
